"use client";

import {
  Button,
  FormInput,
  FormPrice,
  FormRoot,
  FormSelect,
  FormTextarea,
  Label,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeftIcon, BanknoteIcon, PercentIcon } from "lucide-react";
import { z } from "zod";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { useCreateWalletFeeMutation } from "../api/wallet-fee-queries";

const FEE_TYPE_OPTIONS = [
  { value: "marketplace_commission", label: "ຄ່າຄອມ Marketplace" },
  { value: "withdraw_fee", label: "ຄ່າທຳນຽມຖອນເງິນ" },
  { value: "top_up_fee", label: "ຄ່າທຳນຽມເຕີມເງິນ" },
];

const FEE_MODES = [
  {
    value: "percent" as const,
    label: "ເປີເຊັນ",
    description: "ຄິດເປັນ % ຂອງຍອດ",
    icon: PercentIcon,
  },
  {
    value: "flat" as const,
    label: "ຈຳນວນຄົງທີ່",
    description: "ຄິດເປັນຈຳນວນເງິນຕາຍຕົວ",
    icon: BanknoteIcon,
  },
];

const CURRENCY_OPTIONS = [
  { value: "LAK", label: "LAK" },
  { value: "THB", label: "THB" },
  { value: "USD", label: "USD" },
];

const CreateSchema = z.object({
  feeType: z.enum(["marketplace_commission", "withdraw_fee", "top_up_fee"]),
  feeMode: z.enum(["percent", "flat"]),
  value: z.coerce.number().min(0, "ຄ່າຕ້ອງບໍ່ຕ່ຳກວ່າ 0"),
  currency: z.enum(["LAK", "THB", "USD"]),
  minAmount: z.coerce.number().min(0).optional().or(z.literal("")),
  maxAmount: z.coerce.number().min(0).optional().or(z.literal("")),
  description: z.string().max(500).optional(),
});

type CreateValues = z.infer<typeof CreateSchema>;

export function WalletFeeCreatePage() {
  const nav = useNavigate();
  const createMutation = useCreateWalletFeeMutation();

  const methods = RHF.useForm<CreateValues>({
    resolver: zodResolver(CreateSchema) as RHF.Resolver<CreateValues>,
    defaultValues: {
      feeType: "withdraw_fee",
      feeMode: "percent",
      value: 0,
      currency: "LAK",
      minAmount: "",
      maxAmount: "",
      description: "",
    },
  });

  const feeMode = methods.watch("feeMode");

  function onSubmit(data: CreateValues) {
    createMutation.mutate(
      {
        feeType: data.feeType,
        feeMode: data.feeMode,
        value: data.value,
        currency: data.currency,
        minAmount:
          data.minAmount !== "" && data.minAmount != null
            ? Number(data.minAmount)
            : null,
        maxAmount:
          data.maxAmount !== "" && data.maxAmount != null
            ? Number(data.maxAmount)
            : null,
        description: data.description || null,
      },
      {
        onSuccess: () => {
          nav({ to: "/app/wallet/fees" });
        },
      },
    );
  }

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ກະເປົາເງິນ & ການຈ່າຍເງິນ", to: "/app/wallet" },
            { label: "ຄ່າທຳນຽມກະເປົາ", to: "/app/wallet/fees" },
            { label: "ສ້າງກົດໃໝ່" },
          ]}
        />
      </Header>

      <Main>
        <div className="flex flex-wrap items-center justify-between space-y-2">
          <h2 className="font-bold text-2xl tracking-tight">ສ້າງກົດຄ່າທຳນຽມ</h2>
          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/wallet/fees" })}
          >
            <ArrowLeftIcon className="size-4" /> ກັບຄືນ
          </Button>
        </div>

        <div className="mt-6 rounded-xl border bg-card p-6">
          <FormRoot<CreateValues>
            methods={methods}
            onSubmit={onSubmit}
            className="space-y-4"
          >
            <FormSelect
              name="feeType"
              label="ປະເພດຄ່າທຳນຽມ"
              requiredMark
              options={FEE_TYPE_OPTIONS}
            />

            <div className="space-y-2">
              <Label className="font-medium text-sm">
                ໂໝດ & ຄ່າ <span className="text-destructive">*</span>
              </Label>
              <div className="grid gap-3 sm:grid-cols-2">
                {FEE_MODES.map((mode) => {
                  const selected = feeMode === mode.value;
                  const Icon = mode.icon;
                  return (
                    <button
                      key={mode.value}
                      type="button"
                      onClick={() => {
                        methods.setValue("feeMode", mode.value);
                        methods.setValue("value", 0);
                      }}
                      className={`flex flex-col rounded-xl border-2 p-4 text-left transition-all ${
                        selected
                          ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                          : "border-border hover:border-muted-foreground/30"
                      }`}
                    >
                      <div className="mb-3 flex items-center gap-2">
                        <div
                          className={`flex size-9 items-center justify-center rounded-lg ${
                            selected
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted text-muted-foreground"
                          }`}
                        >
                          <Icon className="size-4" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{mode.label}</p>
                          <p className="text-muted-foreground text-xs">
                            {mode.description}
                          </p>
                        </div>
                      </div>
                      {selected && (
                        <div className="mt-1">
                          {mode.value === "percent" ? (
                            <FormInput
                              name="value"
                              label=""
                              type="number"
                              placeholder="ເຊັ່ນ 2.5"
                            />
                          ) : (
                            <FormPrice
                              name="value"
                              label=""
                              currencySymbol="K"
                            />
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <FormSelect
              name="currency"
              label="ສະກຸນເງິນ"
              requiredMark
              options={CURRENCY_OPTIONS}
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <FormPrice
                name="minAmount"
                label="ຂັ້ນຕ່ຳ (ບໍ່ບັງຄັບ)"
                currencySymbol="K"
              />
              <FormPrice
                name="maxAmount"
                label="ຂັ້ນສູງ (ບໍ່ບັງຄັບ)"
                currencySymbol="K"
              />
            </div>

            <FormTextarea
              name="description"
              label="ລາຍລະອຽດ (ບໍ່ບັງຄັບ)"
              placeholder="ອະທິບາຍກົດຄ່າທຳນຽມ..."
            />

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => nav({ to: "/app/wallet/fees" })}
              >
                ຍົກເລີກ
              </Button>
              <Button type="submit" isLoading={createMutation.isPending}>
                ບັນທຶກ
              </Button>
            </div>
          </FormRoot>
        </div>
      </Main>
    </>
  );
}
